import { NumberFormatter, type NumberFormatterOptions, type Formatter } from '@figma/fpl-components';
import { ErrorType, evaluateExpression } from '@/utils/mathParser';
import { getValue, isMixed, type Mixed } from './mixed';

export interface NumberInputFormatterOptions extends NumberFormatterOptions {
  /** Maximum fraction digits shown. Whole numbers never render trailing zeros. */
  decimals?: 0 | 1 | 2;
  /**
   * Called for a relative entry (`2x`, `50%`) on a mixed field, where no single value resolves it.
   * `transform` re-evaluates that entry against any stored value, so the caller can apply it to each
   * of the values the field stands for.
   */
  onRelativeToMixed?: (transform: (current: number) => number) => void;
}

/** Thrown by `parse` so `onParseThrow` can tell the failures apart, as Figma's formatters do. */
class ExpressionError extends Error {
  constructor(readonly errorType: ErrorType) {
    super('Could not parse input');
  }
}

/**
 * Formats and parses the value of a numeric field, accepting the arithmetic the Figma canvas accepts
 * — see `@/utils/mathParser`.
 *
 * Subclasses own a unit: they override `formatUnmixed` to render it and set `allowedUnits` so it is
 * stripped again before an entry is evaluated. That split, and the `Mixed` value type, mirror the
 * editor's `MixedNumberFormatter`.
 *
 * `parse` returns the value the caller stores, so a subclass that displays a different scale (see
 * `PercentFormatter`) converts inside `parse` and `formatUnmixed` rather than at its call sites.
 */
export class NumberInputFormatter extends NumberFormatter<number | Mixed, number> {
  /** `|`-separated units to strip before evaluating an entry, e.g. `%` or `°`. */
  protected allowedUnits = '';

  /** Fraction digits this formatter displays. Read by the pre-FPL input, which has no formatter. */
  readonly decimals: 0 | 1 | 2;

  private readonly onRelativeToMixed: NumberInputFormatterOptions['onRelativeToMixed'];

  /**
   * The base class keeps the options it was given, so they are passed through whole rather than
   * redeclared here: shadowing `opts` would hide `maximumFractionDigits` from its own `format`.
   */
  constructor(options: NumberInputFormatterOptions = {}) {
    const decimals = options.decimals ?? 2;

    super({ ...options, maximumFractionDigits: decimals });

    this.decimals = decimals;
    this.onRelativeToMixed = options.onRelativeToMixed;
  }

  /** The unit this formatter appends, if any. Read by the pre-FPL input, which appends it itself. */
  get displayUnit(): string {
    return '';
  }

  /** A mixed field renders blank, leaving the caller's placeholder to show through. */
  override format(value: number | Mixed): string {
    return isMixed(value) ? '' : this.formatUnmixed(value);
  }

  protected formatUnmixed(value: number): string {
    return super.format(value);
  }

  override parse(value: string, currentValue?: number | Mixed): number {
    const expression = this.allowedUnits ? value.replace(new RegExp(this.allowedUnits, 'g'), '') : value;
    const result = evaluateExpression(expression, getValue(currentValue, undefined));

    if (result.error) {
      throw new ExpressionError(result.error.type);
    }

    return result.value;
  }

  /** Letting FPL nudge a mixed field would submit `NaN`. */
  canIncrement(value: number | Mixed): Formatter.CanIncrement {
    return isMixed(value) ? 'none' : 'all';
  }

  onParseThrow(
    _value: string,
    { error, value }: Formatter.OnParseThrowOptions<number | Mixed>,
  ): Formatter.OnParseThrowCallback<number> | undefined {
    const handler = this.onRelativeToMixed;
    if (!handler) return undefined;
    if (!(error instanceof ExpressionError) || error.errorType !== ErrorType.EVAL_NO_CURRENT_VALUE) return undefined;
    if (!isMixed(value)) return undefined;

    return (transform) => handler(transform);
  }

  /** Identity here; a subclass that rescales overrides both directions. */
  toDisplay(stored: number): number {
    return stored;
  }

  fromDisplay(display: number): number {
    return display;
  }
}
